import {
  CloseOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SettingOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import {
  Button,
  ButtonProps,
  RadioInput,
  Status,
  Tooltip,
} from '@mm/components';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSalesForceCRUDActions } from '@mm/state/actions/SalesForceCRUDActions';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { SalesForceAccount } from '@mm/state/model/SalesForceModel';
import { selectSalesForceConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { colors } from '@mm/utils';
import { notification, Switch } from 'antd';
import { ReactComponent as CustomersAiLogo } from 'assets/icons/customersai-logo.svg';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import pluralize from 'pluralize';
import React, { MouseEvent, ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { ButtonColor } from 'src/common/buttons/PrimaryButton/PrimaryButton';
import { DisabledTagsMessage } from './DisabledTagsMessage';
import { SalesForceConnection } from './SalesForceConnection';
import styles from './SalesForceIntegrations.module.css';
import { useConnectSalesForce } from './use-connect-salesforce';
import { useTagsCommon } from './use-tags-common';

interface Props {
  account: SalesForceAccount;
  oAuthButton: (
    buttonName?: string | ReactNode,
    buttonProps?: Pick<ButtonProps, 'size' | 'color' | 'css' | 'disabled'>,
  ) => ReactNode;
}

enum AudiencesSelect {
  All = 'all',
  Audience = 'audience',
}

export const SalesForceSettings = ({ account, oAuthButton }: Props) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const sfConnections = useSelector(selectSalesForceConnections);
  const [radioValue, setRadioValue] = useState<AudiencesSelect>(
    sfConnections.length === 1 && !sfConnections[0].audienceId
      ? AudiencesSelect.All
      : AudiencesSelect.Audience,
  );
  const radioAllContacts = radioValue === AudiencesSelect.All;
  const accountReadOnly = account.readOnly;

  const accounts = useSelector(
    (state: StoreState) => state.salesForce.accounts,
  );

  const salesForceAccount = accounts?.[0];
  const erroredAccount = salesForceAccount?.status === 'error';

  const errorsArray: { errorCode?: string; message?: string }[] =
    salesForceAccount?.errorInfo && JSON.parse(salesForceAccount?.errorInfo);

  const { update, create, deleteConnection } = useConnectionCRUDActions();
  const { revokeAccess } = useSalesForceCRUDActions();
  const { retrieveSalesForceAccounts } = useConnectSalesForce();
  const { openIntegrationMappingModal } = useModalActions();
  const { tagsEnabled } = useTagsCommon(sfConnections[0].salesforceAccessToken);
  const webhookErrors = sfConnections.flatMap(e => e.webhookErrors || []);

  const updateWebhook = async (
    params: Partial<ConnectionModel>,
    connection: ConnectionModel,
  ) => {
    if (!connection) return;
    const isDeactivatingConnection = !connection.active;

    if (!isDeactivatingConnection && !hasNativeIntegrationsFeature) {
      return;
    }

    await update(facebookPageId, {
      id: connection.id,
      ...params,
    });
  };

  const revoke = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    if (account) {
      const revoked = await revokeAccess(account.id);
      if (revoked) {
        retrieveSalesForceAccounts();
      }
    }
  };

  const onAddClick = () => {
    if (!hasNativeIntegrationsFeature) return;

    create(facebookPageId, {
      name: 'SalesForce Connection',
      connectionKind: 'salesforce',
      salesforceParams: { accessTokenId: account.id },
    });
  };

  const deleteSFWebhook = async (sfConnection: ConnectionModel) => {
    await deleteConnection(facebookPageId, sfConnection);
    notification.success({ message: 'Connection has been deleted' });
  };

  const onSyncAllClick = (_e: React.ChangeEvent<HTMLInputElement>) => {
    setRadioValue(AudiencesSelect.All);
    updateWebhook({ audienceId: null }, sfConnections[0]);
    for (let i = 1; i < sfConnections.length; i += 1) {
      deleteConnection(facebookPageId, sfConnections[i]);
    }
  };

  const showAudiencesBox = !(tagsEnabled === false && radioAllContacts); // hide if tags disabled and all contacts selected

  return (
    <div className={styles.settingsContent}>
      <div className={classNames(styles.box, styles.boxWithHelp)}>
        <div className={styles.boxContent}>
          <div className={styles.heading}>Connect to SalesForce directly</div>
          <div className={styles.connectionBlock}>
            <Status
              status={erroredAccount ? 'Error' : 'Connected'}
              color={erroredAccount ? colors.red : colors.green5}
              className={styles.status}
              css={{ statusIndicator: styles.statusIndicator }}
            />

            {oAuthButton(
              <>
                <SyncOutlined />
                Reconnect
              </>,
              {
                color: 'secondary-blue',
                css: { container: styles.button },
                disabled: accountReadOnly,
              },
            )}
            {account && (
              <Button
                css={{ container: styles.button }}
                color="secondary-blue"
                onClick={revoke}
                disabled={accountReadOnly}
              >
                <CloseOutlined />
                Disconnect
              </Button>
            )}
            {tagsEnabled === false && <DisabledTagsMessage />}
          </div>
          <div className={styles.error}>
            {errorsArray?.map(item => item?.message || item)}
          </div>
        </div>
      </div>

      <div
        className={classNames(styles.box, {
          [styles.boxDisabled]: accountReadOnly,
        })}
      >
        <div className={styles.boxWithHelp}>
          <div className={classNames(styles.heading, styles.headingWithSwitch)}>
            {radioAllContacts && (
              <>
                Contact sync
                <Switch
                  defaultChecked={sfConnections[0].active}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  className={styles.switch}
                  onChange={active =>
                    updateWebhook({ active }, sfConnections[0])
                  }
                  disabled={!sfConnections[0]}
                />
                <SettingOutlined
                  onClick={() =>
                    openIntegrationMappingModal({
                      connection: sfConnections[0],
                    })
                  }
                />
              </>
            )}

            {webhookErrors.length > 0 && (
              <Tooltip
                withoutLine
                useHover={true}
                anchorElement={
                  <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                }
                css={{ container: styles.tooltip }}
              >
                <div className={styles.errors}>
                  {webhookErrors.map(error => (
                    <span key={uniqueId(error.errorDetails)}>
                      {error.errorDetails} -{' '}
                      {`${error.quantity} ${pluralize(
                        'error',
                        error.quantity,
                      )}`}
                    </span>
                  ))}
                </div>
              </Tooltip>
            )}
          </div>
        </div>
        <div className={styles.radioInputs}>
          <RadioInput
            name="all"
            value="all"
            label="Sync all Customers.ai contacts to SalesForce"
            onChange={onSyncAllClick}
            checked={radioAllContacts}
          />
          <RadioInput
            name="audience"
            value="audience"
            label="Choose an audience to sync from "
            onChange={() => setRadioValue(AudiencesSelect.Audience)}
            checked={!radioAllContacts}
          />
        </div>
        {showAudiencesBox && (
          <div
            className={classNames(
              styles.connectionsContainer,
              radioAllContacts ? styles.gridAll : styles.gridSingle,
              tagsEnabled === false && styles.gridTwoColumns,
            )}
          >
            {!radioAllContacts && (
              <span
                className={classNames(
                  styles.heading,
                  styles.audienceSelect,
                  styles.gridItem,
                )}
              >
                <CustomersAiLogo className={styles.gridIcon} />
                Customers.ai Audience
              </span>
            )}
            {tagsEnabled && (
              <span className={classNames(styles.heading, styles.gridItem)}>
                Add Tag to contacts in SalesForce
              </span>
            )}
            {!radioAllContacts && (
              <span className={classNames(styles.heading, styles.gridItem)}>
                Actions
              </span>
            )}
            {sfConnections.map(sfConnection => (
              <SalesForceConnection
                key={sfConnection.id}
                connection={sfConnection}
                radioAllContacts={radioAllContacts}
                facebookPageId={facebookPageId}
                updateWebhook={updateWebhook}
                deleteWebhook={deleteSFWebhook}
              />
            ))}

            <div>
              {!radioAllContacts && (
                <Button
                  color={ButtonColor.secondaryBlue}
                  onClick={onAddClick}
                  leftIcon={<PlusOutlined />}
                >
                  Add
                </Button>
              )}
              {tagsEnabled === true && (
                <div className={styles.note}>
                  All leads will be tagged “Customers.ai”
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
